<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  addContribution,
  createGoal,
  deleteContribution,
  deleteGoal,
  fromMinor,
  getGoal,
  listContributions,
  listGoals,
  money,
  updateGoal,
  type Contribution,
  type Goal,
} from '../api/planning'
import { useSession } from '../stores/session'
import { compact, useForm } from '../lib/form'
import { formatMoney, isoDaysAgo } from '../lib/money'
import UiCard from '../components/UiCard.vue'
import UiButton from '../components/UiButton.vue'
import UiBadge from '../components/UiBadge.vue'
import UiField from '../components/UiField.vue'
import UiDrawer from '../components/UiDrawer.vue'
import ErrorBanner from '../components/ErrorBanner.vue'
import EmptyState from '../components/EmptyState.vue'

const { t, locale } = useI18n()
const session = useSession()
const load = useForm()
const create = useForm()
const action = useForm()

const goals = ref<Goal[]>([])
const showArchived = ref(false)
const visible = computed(() => goals.value.filter((g) => showArchived.value || !g.archived))
watch(() => session.workspace?.id, () => load.submit(async () => (goals.value = await listGoals())), { immediate: true })

const fmt = (m: { amount_minor: number; currency: string }) => formatMoney(m, locale.value)
const bar = (g: Goal) => Math.min(100, Math.max(0, Number(g.progress_pct)))

// ---- create ----
const showCreate = ref(false)
const form = reactive({ name: '', target: '', currency: 'EUR', target_date: '' })
async function onCreate() {
  const ok = await create.submit(async () => {
    const g = await createGoal(compact({ name: form.name, target: money(form.target, form.currency), target_date: form.target_date }))
    goals.value = [...goals.value, g]
  })
  if (ok) {
    Object.assign(form, { name: '', target: '', target_date: '' })
    showCreate.value = false
  }
}

// ---- detail ----
const selected = ref<Goal | null>(null)
const contributions = ref<Contribution[]>([])
const edit = reactive({ name: '', target: '', target_date: '' })
const contrib = reactive({ date: isoDaysAgo(0), amount: '', note: '' })
const detail = useForm()

function replace(g: Goal) {
  goals.value = goals.value.map((x) => (x.id === g.id ? g : x))
  selected.value = g
}
async function open(g: Goal) {
  selected.value = g
  contributions.value = []
  Object.assign(edit, { name: g.name, target: fromMinor(g.target), target_date: g.target_date ?? '' })
  Object.assign(contrib, { date: isoDaysAgo(0), amount: '', note: '' })
  await detail.submit(async () => (contributions.value = await listContributions(g.id)))
}
const saveGoal = (g: Goal) =>
  action.submit(async () => replace(await updateGoal(g.id, { name: edit.name, target: money(edit.target, g.target.currency), target_date: edit.target_date })))
const toggleArchive = (g: Goal) => action.submit(async () => replace(await updateGoal(g.id, { archived: !g.archived })))
async function removeGoal(g: Goal) {
  if (!confirm(t('savings.deleteConfirm', { name: g.name }))) return
  if (await action.submit(() => deleteGoal(g.id))) {
    goals.value = goals.value.filter((x) => x.id !== g.id)
    selected.value = null
  }
}
async function onContribute(g: Goal) {
  const ok = await action.submit(async () => {
    await addContribution(g.id, compact({ date: contrib.date, amount: money(contrib.amount, g.target.currency), note: contrib.note }))
    ;[contributions.value] = await Promise.all([listContributions(g.id), getGoal(g.id).then(replace)])
  })
  if (ok) Object.assign(contrib, { amount: '', note: '' })
}
const removeContribution = (g: Goal, c: Contribution) =>
  action.submit(async () => {
    await deleteContribution(g.id, c.id)
    contributions.value = contributions.value.filter((x) => x.id !== c.id)
    replace(await getGoal(g.id))
  })
</script>

<template>
  <div class="page wide">
    <header class="page-header">
      <div>
        <h1>{{ $t('savings.title') }}</h1>
        <p class="muted">{{ $t('savings.subtitle') }}</p>
      </div>
      <UiButton v-if="session.canWrite && !showCreate" data-testid="new-goal" @click="showCreate = true">{{ $t('savings.new') }}</UiButton>
    </header>

    <UiCard v-if="showCreate" :title="$t('savings.new')">
      <form class="grid-2" data-testid="create-goal" @submit.prevent="onCreate">
        <UiField :label="$t('savings.name')" :error="create.fieldErr('name')">
          <input v-model.trim="form.name" name="name" required maxlength="100" />
        </UiField>
        <UiField :label="$t('savings.target')" :error="create.fieldErr('target.amount_minor')">
          <input v-model.trim="form.target" name="target" required inputmode="decimal" pattern="\d+([.,]\d{1,2})?" />
        </UiField>
        <UiField :label="$t('accounts.currency')" :error="create.fieldErr('target.currency')">
          <input v-model.trim="form.currency" name="currency" required pattern="[A-Za-z]{3}" maxlength="3" />
        </UiField>
        <UiField :label="$t('savings.targetDate')" :error="create.fieldErr('target_date')">
          <input v-model="form.target_date" name="target_date" type="date" />
        </UiField>
        <div class="row">
          <UiButton type="submit" :loading="create.busy.value">{{ $t('common.create') }}</UiButton>
          <UiButton variant="ghost" @click="showCreate = false">{{ $t('common.cancel') }}</UiButton>
        </div>
      </form>
      <ErrorBanner :error="create.error.value" />
    </UiCard>

    <label class="row small"><input v-model="showArchived" type="checkbox" class="cb" /> {{ $t('savings.showArchived') }}</label>
    <ErrorBanner :error="load.error.value" />

    <p v-if="load.busy.value && !goals.length" class="muted">{{ $t('common.loading') }}</p>
    <EmptyState v-else-if="!visible.length" :title="$t('savings.empty')" :message="$t('savings.emptyText')" />
    <div v-else class="grid-2" data-testid="goal-list">
      <UiCard v-for="g in visible" :key="g.id">
        <button type="button" class="goal" data-testid="goal-card" @click="open(g)">
          <div class="row">
            <strong>{{ g.name }}</strong>
            <UiBadge v-if="g.archived" variant="outline">{{ $t('savings.archived') }}</UiBadge>
            <span class="spacer" />
            <span class="small muted">{{ g.progress_pct }}%</span>
          </div>
          <div class="bar" role="progressbar" :aria-valuenow="Math.round(bar(g))" aria-valuemin="0" aria-valuemax="100" :aria-label="g.name">
            <div class="fill" :style="{ width: `${bar(g)}%` }" />
          </div>
          <p class="num">{{ fmt(g.saved) }} / {{ fmt(g.target) }}</p>
          <p class="small muted">
            {{ $t('savings.monthlyNeeded') }}: {{ fmt(g.monthly_needed) }}
            <template v-if="g.target_date"> · {{ $t('savings.targetDate') }}: {{ g.target_date }}</template>
          </p>
        </button>
      </UiCard>
    </div>

    <UiDrawer v-if="selected" :title="selected.name" @close="selected = null">
      <ErrorBanner :error="action.error.value ?? detail.error.value" />
      <dl class="kv">
        <dt>{{ $t('savings.saved') }}</dt>
        <dd data-testid="goal-saved">{{ fmt(selected.saved) }} ({{ selected.progress_pct }}%)</dd>
        <dt>{{ $t('savings.target') }}</dt>
        <dd>{{ fmt(selected.target) }}</dd>
        <dt>{{ $t('savings.monthlyNeeded') }}</dt>
        <dd>{{ fmt(selected.monthly_needed) }}</dd>
        <dt>{{ $t('savings.targetDate') }}</dt>
        <dd>{{ selected.target_date ?? '—' }}</dd>
      </dl>

      <form v-if="session.canWrite" class="grid-2" @submit.prevent="saveGoal(selected)">
        <UiField :label="$t('savings.name')" :error="action.fieldErr('name')">
          <input v-model.trim="edit.name" required maxlength="100" />
        </UiField>
        <UiField :label="`${$t('savings.target')}, ${selected.target.currency}`" :error="action.fieldErr('target.amount_minor')">
          <input v-model.trim="edit.target" required inputmode="decimal" pattern="\d+([.,]\d{1,2})?" />
        </UiField>
        <UiField :label="$t('savings.targetDate')" :error="action.fieldErr('target_date')">
          <input v-model="edit.target_date" type="date" />
        </UiField>
        <div class="row">
          <UiButton type="submit" variant="outline" size="sm" :loading="action.busy.value">{{ $t('common.save') }}</UiButton>
          <UiButton variant="outline" size="sm" @click="toggleArchive(selected)">{{ selected.archived ? $t('savings.unarchive') : $t('savings.archive') }}</UiButton>
          <UiButton variant="destructive" size="sm" @click="removeGoal(selected)">{{ $t('common.delete') }}</UiButton>
        </div>
      </form>

      <h3>{{ $t('savings.contributions') }}</h3>
      <form v-if="session.canWrite" class="row" data-testid="add-contribution" @submit.prevent="onContribute(selected)">
        <input v-model="contrib.date" type="date" class="auto" required :aria-label="$t('savings.date')" />
        <input v-model.trim="contrib.amount" name="amount" class="amt" required inputmode="decimal" pattern="-?\d+([.,]\d{1,2})?"
          :placeholder="$t('budget.amount')" :aria-label="$t('budget.amount')" />
        <input v-model.trim="contrib.note" class="grow" maxlength="200" :placeholder="$t('bills.note')" :aria-label="$t('bills.note')" />
        <UiButton type="submit" size="sm" :loading="action.busy.value">{{ $t('savings.addContribution') }}</UiButton>
      </form>
      <p class="small muted">{{ $t('savings.withdrawHint') }}</p>
      <p v-if="detail.busy.value" class="muted">{{ $t('common.loading') }}</p>
      <p v-else-if="!contributions.length" class="muted small">—</p>
      <table v-else class="table" data-testid="contribution-list">
        <tbody>
          <tr v-for="c in contributions" :key="c.id">
            <td>{{ c.date }}</td>
            <td class="small muted">{{ c.note ?? '' }}</td>
            <td :class="['num', c.amount.amount_minor < 0 ? 'out' : 'in']">{{ formatMoney(c.amount, locale, true) }}</td>
            <td v-if="session.canWrite" class="num">
              <UiButton variant="ghost" size="sm" :aria-label="$t('common.delete')" @click="removeContribution(selected, c)">×</UiButton>
            </td>
          </tr>
        </tbody>
      </table>
    </UiDrawer>
  </div>
</template>

<style scoped>
.wide { max-width: 1100px; }
.goal { all: unset; display: flex; flex-direction: column; gap: 0.4rem; width: 100%; cursor: pointer; }
.goal:focus-visible { outline: 2px solid var(--accent); outline-offset: 4px; }
.bar { height: 10px; border-radius: 999px; background: var(--border); overflow: hidden; }
.fill { height: 100%; background: var(--accent); border-radius: 999px; }
.num { font-variant-numeric: tabular-nums; }
.in { color: var(--gain); }
.out { color: var(--loss); }
.cb, .auto { width: auto; }
.amt { width: 8rem; }
.grow { flex: 1; min-width: 120px; }
</style>
